import { AlertCircle, FileText, RefreshCw } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { paymentApi } from "../../../services/payment.api";
import { FacilityInvoice, PageResponse } from "../../../types/payment";
import { formatMoney } from "../../../utils/format.utils";
import {
  formatBillingPeriod,
  formatDateOnly,
  invoiceDisplayStatus,
  isInvoicePayable,
} from "../../../utils/invoice.utils";
import { InvoiceDetail } from "./InvoiceDetail";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";

export const INVOICES_PAGE_SIZE = 20;

interface InvoicesTabProps {
  facilityId: number | undefined;
  facilityName?: string | null;
  /** Starts the Licence page's payment flow; resolves true when it redirects to checkout. */
  onPay: (invoiceId: number) => Promise<boolean>;
  /** True while another checkout is open (the "payment in progress" overlay is shown). */
  payDisabled?: boolean;
}

export const InvoicesTab: React.FC<InvoicesTabProps> = ({ facilityId, facilityName, onPay, payDisabled = false }) => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(0);
  const [data, setData] = useState<PageResponse<FacilityInvoice> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [payingId, setPayingId] = useState<number | null>(null);
  const [selected, setSelected] = useState<FacilityInvoice | null>(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(false);
    try {
      setData(await paymentApi.getFacilityInvoices(facilityId, page, INVOICES_PAGE_SIZE));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [facilityId, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const closeDetail = useCallback(() => setSelected(null), []);

  const handlePay = async (invoiceId: number) => {
    if (payingId !== null || payDisabled) return;
    setPayingId(invoiceId);
    let redirected = false;
    try {
      redirected = await onPay(invoiceId);
    } finally {
      // On redirect the spinner stays until the page unloads.
      if (!redirected) {
        setPayingId(null);
        void load();
      }
    }
  };

  const invoices = data?.content ?? [];
  const totalPages = Math.max(1, data?.totalPages ?? 1);

  return (
    <div className="space-y-4">
      {loading && <InvoicesSkeleton />}

      {!loading && error && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 p-4 border bg-danger-50 border-danger-200 rounded-xl text-danger-700"
        >
          <AlertCircle className="flex-shrink-0 w-5 h-5" />
          <span className="flex-1 text-sm">{t("licence.invoices.error")}</span>
          <button
            onClick={() => void load()}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-white border rounded-lg border-danger-300 hover:bg-danger-100"
          >
            <RefreshCw className="w-4 h-4" />
            {t("licence.invoices.retry")}
          </button>
        </div>
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 space-y-3 text-neutral-400">
          <FileText className="w-12 h-12" />
          <p className="text-sm">{t("licence.invoices.empty")}</p>
        </div>
      )}

      {!loading && !error && invoices.length > 0 && (
        <div className="overflow-hidden bg-white border shadow-sm border-neutral-200 rounded-2xl">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs font-medium tracking-wide text-left uppercase bg-neutral-50 text-neutral-500">
                <tr>
                  <th className="px-4 py-3">{t("licence.invoices.columns.number")}</th>
                  <th className="px-4 py-3">{t("licence.invoices.columns.period")}</th>
                  <th className="px-4 py-3 text-right">{t("licence.invoices.columns.amount")}</th>
                  <th className="px-4 py-3">{t("licence.invoices.columns.status")}</th>
                  <th className="px-4 py-3">{t("licence.invoices.columns.dueDate")}</th>
                  <th className="px-4 py-3">{t("licence.invoices.columns.paidDate")}</th>
                  <th className="px-4 py-3">
                    <span className="sr-only">{t("licence.invoices.columns.actions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {invoices.map((invoice) => {
                  const status = invoiceDisplayStatus(invoice.status, invoice.dueDate);
                  return (
                    <tr
                      key={invoice.id}
                      data-testid={`invoice-row-${invoice.id}`}
                      onClick={() => setSelected(invoice)}
                      className="cursor-pointer hover:bg-neutral-50"
                    >
                      <td className="px-4 py-3 font-medium whitespace-nowrap text-primary-600">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelected(invoice);
                          }}
                          className="hover:underline"
                        >
                          #{invoice.id}
                        </button>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-neutral-700">
                        {formatBillingPeriod(invoice.billingPeriod, i18n.language, invoice.periodMonths)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-right whitespace-nowrap text-neutral-900">
                        {formatMoney(invoice.amount, i18n.language)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <InvoiceStatusBadge status={status} rawStatus={invoice.status} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-neutral-600">
                        {formatDateOnly(invoice.dueDate, i18n.language)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-neutral-600">
                        {formatDateOnly(invoice.paidDate, i18n.language)}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {isInvoicePayable(status) && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void handlePay(invoice.id);
                            }}
                            disabled={payingId !== null || payDisabled}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {payingId === invoice.id && (
                              <span className="w-3 h-3 border-b-2 border-white rounded-full animate-spin" />
                            )}
                            {t("licence.invoices.pay")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm border-t border-neutral-100">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 rounded-lg border border-neutral-200 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("pagination.previous")}
              </button>
              <span className="text-neutral-500">
                {t("pagination.pageOf", { current: page + 1, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 rounded-lg border border-neutral-200 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("pagination.next")}
              </button>
            </div>
          )}
        </div>
      )}

      {selected && (
        <InvoiceDetail
          invoice={selected}
          facilityName={selected.facilityName ?? facilityName}
          onClose={closeDetail}
        />
      )}
    </div>
  );
};

function InvoicesSkeleton() {
  return (
    <div
      data-testid="invoices-skeleton"
      aria-busy="true"
      className="p-4 space-y-3 bg-white border border-neutral-200 rounded-2xl"
    >
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="h-8 rounded animate-pulse bg-neutral-100" />
      ))}
    </div>
  );
}
